import { Link, router, usePathname, type Href } from 'expo-router';
import {
  BellIcon,
  HomeIcon,
  MenuIcon,
  MessageCircleIcon,
  RouteIcon,
  ScanLineIcon,
  ShieldIcon,
  SquarePlusIcon,
  UserIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Modal, Pressable, View } from 'react-native';

import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { Avatar, AvatarFallback, AvatarImage } from '@sixam/ui/avatar';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { BRAND } from '@sixam/config/brand';
import { getInitials } from '@sixam/supabase/auth';
import { cn } from '@sixam/ui/utils';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';
import {
  openOperatorCheckIn,
  useActiveOperatorCheckIn,
} from '@/components/events/live-check-in-entry';

const baseNavItems = [
  { href: '/app/home' as Href, icon: HomeIcon, label: 'Home' },
  { href: '/app/me' as Href, icon: UserIcon, label: 'Me' },
] as const;

export function MemberWebNavigation() {
  const pathname = usePathname();
  const { isAdmin, profile, session } = useSession();
  const { isAnyTenantAdmin } = useTenant();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const routeLibraryEnabled = useReleaseFeatureFlag('routeLibrary') === true;
  const activeCheckInEvent = useActiveOperatorCheckIn();
  const canCreateEvent = Boolean(session);
  const canOpenAdmin = isAdmin || isAnyTenantAdmin;

  const email = session?.user.email ?? '';
  const displayName = profile?.full_name ?? email ?? '6am member';
  const initials = getInitials(email || displayName);
  const avatarUrl =
    profile?.avatar_url ?? (session?.user.user_metadata.avatar_url as string | undefined);
  return (
    <MemberWebNavigationFrame
      avatarUrl={avatarUrl}
      canCreateEvent={canCreateEvent}
      canOpenAdmin={canOpenAdmin}
      displayName={displayName}
      drawerOpen={drawerOpen}
      initials={initials}
      onCloseDrawer={() => setDrawerOpen(false)}
      onCheckIn={activeCheckInEvent ? () => openOperatorCheckIn(activeCheckInEvent.id) : undefined}
      onCreate={() => router.push('/app/events/new')}
      onOpenAdmin={() => router.push('/admin')}
      onOpenDrawer={() => setDrawerOpen(true)}
      pathname={pathname}
      showRoutes={routeLibraryEnabled}
    />
  );
}

export function MemberWebNavigationFrame({
  avatarUrl,
  canCreateEvent = false,
  canOpenAdmin = false,
  displayName,
  drawerOpen,
  initials,
  onCloseDrawer,
  onCheckIn,
  onCreate,
  onOpenAdmin,
  onOpenDrawer,
  pathname,
  showRoutes = false,
}: Readonly<{
  avatarUrl?: string | null;
  canCreateEvent?: boolean;
  canOpenAdmin?: boolean;
  displayName: string;
  drawerOpen: boolean;
  initials: string;
  onCloseDrawer: () => void;
  onCheckIn?: () => void;
  onCreate: () => void;
  onOpenAdmin: () => void;
  onOpenDrawer: () => void;
  pathname: string;
  showRoutes?: boolean;
}>) {
  const navItems = showRoutes
    ? [
        ...baseNavItems.slice(0, 1),
        { href: '/app/routes' as Href, icon: RouteIcon, label: 'Routes' },
        ...baseNavItems.slice(1),
      ]
    : baseNavItems;
  return (
    <>
      <View className="h-[76px] shrink-0 border-b border-white/10 bg-secondary px-4 xl:px-8">
        <View className="mx-auto h-full w-full max-w-[1440px] flex-row items-center gap-3 xl:gap-6">
          <NavIconButton accessibilityLabel="Open navigation" onPress={onOpenDrawer}>
            <Icon as={MenuIcon} className="size-6 text-white/90" />
          </NavIconButton>

          <View className="ml-auto flex-row items-center gap-3 xl:gap-5">
            <NavIconButton accessibilityLabel="Open messages" href="/app/me">
              <Icon as={MessageCircleIcon} className="size-6 text-white/90" />
            </NavIconButton>
            {onCheckIn ? (
              <NavTextButton accessibilityLabel="Check in active event" onPress={onCheckIn}>
                <Icon as={ScanLineIcon} className="size-6 text-white/90" />
                <Text className="font-sans text-xl font-bold text-white">Check in</Text>
              </NavTextButton>
            ) : null}
            {canCreateEvent ? (
              <NavTextButton accessibilityLabel="Create" onPress={onCreate}>
                <Icon as={SquarePlusIcon} className="size-6 text-white/90" />
                <Text className="hidden font-sans text-xl font-bold text-white xl:flex">
                  Create
                </Text>
              </NavTextButton>
            ) : null}
            <NavIconButton accessibilityLabel="Open notifications" href="/app/me">
              <Icon as={BellIcon} className="size-6 text-white/90" />
            </NavIconButton>
            <Link href="/app/me" asChild>
              <Pressable accessibilityLabel="Open account menu" className="active:opacity-80">
                <Avatar alt={`${displayName}'s avatar`} className="size-11 border border-white/20">
                  {avatarUrl ? <AvatarImage source={{ uri: avatarUrl }} /> : null}
                  <AvatarFallback className="bg-primary">
                    <Text className="font-sans text-base font-bold text-white">{initials}</Text>
                  </AvatarFallback>
                </Avatar>
              </Pressable>
            </Link>
          </View>
        </View>
      </View>

      <Modal animationType="fade" onRequestClose={onCloseDrawer} transparent visible={drawerOpen}>
        <Pressable className="flex-1 bg-black/45" onPress={onCloseDrawer}>
          <Pressable className="h-full w-[360px] overflow-hidden bg-background">
            <View className="gap-4 p-5">
              <View className="flex-row items-center justify-between">
                <View>
                  <Text variant="label" className="text-muted-foreground">
                    6am BKK
                  </Text>
                  <Text variant="h3">Navigation</Text>
                </View>
                <NavIconButton
                  accessibilityLabel="Close navigation"
                  onPress={onCloseDrawer}
                  surface="light"
                >
                  <Icon as={XIcon} className="size-6 text-foreground" />
                </NavIconButton>
              </View>

              <View className="gap-1">
                {navItems.map((item) => {
                  const active =
                    pathname === item.href ||
                    (item.href !== '/app' && pathname.startsWith(String(item.href)));

                  return (
                    <DrawerLink
                      key={String(item.href)}
                      active={active}
                      href={item.href}
                      icon={item.icon}
                      label={item.label}
                      onPress={onCloseDrawer}
                    />
                  );
                })}
                {canOpenAdmin ? (
                  <Pressable
                    className="flex-row items-center gap-3 border-b border-border py-4 active:opacity-70"
                    onPress={onOpenAdmin}
                  >
                    <Icon as={ShieldIcon} className="size-5 text-primary" />
                    <Text className="text-lg font-bold">Admin view</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function NavIconButton({
  children,
  href,
  surface = 'dark',
  ...props
}: React.ComponentProps<typeof Pressable> & {
  href?: Href;
  surface?: 'dark' | 'light';
}) {
  const className = cn(
    'size-12 shrink-0 items-center justify-center rounded-full active:opacity-80',
    surface === 'dark' ? 'hover:bg-white/10' : 'border border-border bg-background hover:bg-muted'
  );

  if (href) {
    return (
      <Link href={href} asChild>
        <Pressable className={className} {...props}>
          {children}
        </Pressable>
      </Link>
    );
  }

  return (
    <Pressable className={className} {...props}>
      {children}
    </Pressable>
  );
}

function NavTextButton({ children, ...props }: React.ComponentProps<typeof Pressable>) {
  return (
    <Pressable
      className="h-12 flex-row items-center gap-3 rounded-full px-2 hover:bg-white/10 active:opacity-80"
      {...props}
    >
      {children}
    </Pressable>
  );
}

function DrawerLink({
  active,
  href,
  icon,
  label,
  onPress,
}: Readonly<{
  active: boolean;
  href: Href;
  icon: LucideIcon;
  label: string;
  onPress: () => void;
}>) {
  return (
    <Link href={href} asChild>
      <Pressable
        className={cn(
          'flex-row items-center gap-3 border-b border-border py-4 active:opacity-70',
          active && 'border-primary'
        )}
        onPress={onPress}
      >
        <Icon
          as={icon}
          className={cn('size-5', active ? 'text-primary' : 'text-foreground')}
          color={active ? BRAND.color.orange : undefined}
        />
        <Text className={cn('text-lg font-bold', active && 'text-primary')}>{label}</Text>
      </Pressable>
    </Link>
  );
}
